import { LoadingSpinner, QueryErrorAlert } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import type { StatementRowView, StatementView } from '../../utils/statementRows';
import { AbsorbedFeesCard } from './AbsorbedFeesCard';
import { PlanBalanceCard } from './PlanBalanceCard';
import { PlanCostCard } from './PlanCostCard';
import { ScheduledTotalsCard } from './ScheduledTotalsCard';
import { StatementTransactions } from './StatementTransactions';
import { RelatedTransactions } from './RelatedTransactions';

interface Props {
    view: StatementView | null;
    isLoading: boolean;
    isError: boolean;
    notActivated: boolean;
    rows: StatementRowView[];
    futureCount: number;
    showUnreconciled: boolean;
    onShowUnreconciledChange: (next: boolean) => void;
    onRetry: () => void;
}

export function AgreementFinancialsTab(props: Props) {
    return (
        <FinancialsBody {...props} />
    );
}

function FinancialsBody({ view, isLoading, isError, notActivated, rows, futureCount, showUnreconciled, onShowUnreconciledChange, onRetry }: Props) {
    if (isLoading) return <LoadingSpinner message="Loading financials" />;

    if (isError) {
        return (
            <QueryErrorAlert message="The ledger is unavailable, so financials could not be loaded." onRetry={onRetry} />
        );
    }

    if (notActivated) return <p className="text-sm text-mp-muted">Financials appear once the agreement is active</p>;
    if (!view) return null;

    return (
        <Statement
            view={view}
            rows={rows}
            futureCount={futureCount}
            showUnreconciled={showUnreconciled}
            onShowUnreconciledChange={onShowUnreconciledChange}
        />
    );
}

interface StatementProps {
    view: StatementView;
    rows: StatementRowView[];
    futureCount: number;
    showUnreconciled: boolean;
    onShowUnreconciledChange: (next: boolean) => void;
}

function Statement({ view, rows, futureCount, showUnreconciled, onShowUnreconciledChange }: StatementProps) {
    const { absorbedFees } = view;

    const baseCards = (
        <>
            <PlanCostCard planCost={view.planCost} currency={view.currency} />
            <div className="space-y-4">
                <PlanBalanceCard totals={view.balanceTotals} currency={view.currency} />
                <ScheduledTotalsCard totals={view.scheduledTotals} currency={view.currency} />
            </div>
        </>
    );
    const twoColumnGrid = <div className="grid gap-4 md:grid-cols-2">{baseCards}</div>;

    return (
        <section aria-label="Financials" className="space-y-4">
            {absorbedFees ? (
                <PermissionGuard permission={{ resource: 'agreement', action: 'absorbed_fee.read' }} showFallback fallback={twoColumnGrid}>
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {baseCards}
                        <AbsorbedFeesCard view={absorbedFees} currency={view.currency} />
                    </div>
                </PermissionGuard>
            ) : (
                twoColumnGrid
            )}
            <StatementTransactions
                view={view}
                rows={rows}
                futureCount={futureCount}
                showUnreconciled={showUnreconciled}
                onShowUnreconciledChange={onShowUnreconciledChange}
            />
            <RelatedTransactions rows={view.related} currency={view.currency} />
        </section>
    );
}
